/**
 * Unified Error Handling System
 * Provides comprehensive error types and utilities
 */

import { ServiceError, ServiceErrorCode } from "./types";

/**
 * Base service error class with enhanced debugging capabilities
 */
export class UnifiedServiceError extends Error implements ServiceError {
  public readonly code: ServiceErrorCode;
  public readonly status?: number;
  public readonly details?: any;
  public readonly service?: string;
  public readonly context?: string;
  public readonly timestamp: string;
  public readonly requestId?: string;
  public readonly retry: boolean;

  constructor(
    message: string,
    code: ServiceErrorCode = ServiceErrorCode.UNKNOWN_ERROR,
    options: {
      status?: number;
      details?: any;
      service?: string;
      context?: string;
      requestId?: string;
      retry?: boolean;
      cause?: Error;
    } = {},
  ) {
    super(message);

    this.name = "UnifiedServiceError";
    this.code = code;
    this.status = options.status;
    this.details = options.details;
    this.service = options.service;
    this.context = options.context;
    this.requestId = options.requestId;
    this.retry = options.retry ?? this.isRetryable();
    this.timestamp = new Date().toISOString();

    // Maintain stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, UnifiedServiceError);
    }

    // Chain the cause if provided
    if (options.cause) {
      this.cause = options.cause;
    }
  }

  /**
   * Determine if error is retryable based on code
   */
  private isRetryable(): boolean {
    const retryableCodes = [
      ServiceErrorCode.NETWORK_ERROR,
      ServiceErrorCode.TIMEOUT_ERROR,
      ServiceErrorCode.RATE_LIMIT_ERROR,
      ServiceErrorCode.SERVER_ERROR,
    ];
    return retryableCodes.includes(this.code);
  }

  /**
   * Convert to JSON for logging/serialization
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      status: this.status,
      details: this.details,
      service: this.service,
      context: this.context,
      timestamp: this.timestamp,
      requestId: this.requestId,
      retry: this.retry,
      stack: this.stack,
    };
  }

  /**
   * Create a user-friendly error message
   */
  getUserMessage(): string {
    switch (this.code) {
      case ServiceErrorCode.NETWORK_ERROR:
        return "Network connection failed. Please check your internet connection.";
      case ServiceErrorCode.TIMEOUT_ERROR:
        return "Request timed out. Please try again.";
      case ServiceErrorCode.AUTHENTICATION_ERROR:
        return "Authentication failed. Please log in again.";
      case ServiceErrorCode.AUTHORIZATION_ERROR:
        return "You do not have permission to perform this action.";
      case ServiceErrorCode.NOT_FOUND_ERROR:
        return "The requested resource was not found.";
      case ServiceErrorCode.RATE_LIMIT_ERROR:
        return "Too many requests. Please wait a moment and try again.";
      case ServiceErrorCode.VALIDATION_ERROR:
        return "Invalid data provided. Please check your input.";
      case ServiceErrorCode.SERVER_ERROR:
        return "Server error occurred. Please try again later.";
      default:
        return "An unexpected error occurred. Please try again.";
    }
  }
}

/**
 * Specific error classes for different scenarios
 */
export class NetworkError extends UnifiedServiceError {
  constructor(
    message: string,
    options: Omit<
      ConstructorParameters<typeof UnifiedServiceError>[2],
      "code"
    > = {},
  ) {
    super(message, ServiceErrorCode.NETWORK_ERROR, options);
  }
}

export class TimeoutError extends UnifiedServiceError {
  constructor(
    message: string,
    timeout: number,
    options: Omit<
      ConstructorParameters<typeof UnifiedServiceError>[2],
      "code"
    > = {},
  ) {
    super(
      `${message} (timeout: ${timeout}ms)`,
      ServiceErrorCode.TIMEOUT_ERROR,
      {
        ...options,
        details: { ...options.details, timeout },
      },
    );
  }
}

export class ValidationError extends UnifiedServiceError {
  constructor(
    message: string,
    validationErrors: any[],
    options: Omit<
      ConstructorParameters<typeof UnifiedServiceError>[2],
      "code"
    > = {},
  ) {
    super(message, ServiceErrorCode.VALIDATION_ERROR, {
      ...options,
      details: { ...options.details, validationErrors },
      retry: false,
    });
  }
}

export class AuthenticationError extends UnifiedServiceError {
  constructor(
    message: string = "Authentication required",
    options: Omit<
      ConstructorParameters<typeof UnifiedServiceError>[2],
      "code"
    > = {},
  ) {
    super(message, ServiceErrorCode.AUTHENTICATION_ERROR, {
      ...options,
      status: 401,
      retry: false,
    });
  }
}

export class AuthorizationError extends UnifiedServiceError {
  constructor(
    message: string = "Insufficient permissions",
    options: Omit<
      ConstructorParameters<typeof UnifiedServiceError>[2],
      "code"
    > = {},
  ) {
    super(message, ServiceErrorCode.AUTHORIZATION_ERROR, {
      ...options,
      status: 403,
      retry: false,
    });
  }
}

export class NotFoundError extends UnifiedServiceError {
  constructor(
    resource: string,
    id?: string | number,
    options: Omit<
      ConstructorParameters<typeof UnifiedServiceError>[2],
      "code"
    > = {},
  ) {
    const message = id
      ? `${resource} with ID ${id} not found`
      : `${resource} not found`;
    super(message, ServiceErrorCode.NOT_FOUND_ERROR, {
      ...options,
      status: 404,
      retry: false,
      details: { ...options.details, resource, id },
    });
  }
}

export class RateLimitError extends UnifiedServiceError {
  constructor(
    retryAfter?: number,
    options: Omit<
      ConstructorParameters<typeof UnifiedServiceError>[2],
      "code"
    > = {},
  ) {
    const message = retryAfter
      ? `Rate limit exceeded. Retry after ${retryAfter} seconds.`
      : "Rate limit exceeded.";

    super(message, ServiceErrorCode.RATE_LIMIT_ERROR, {
      ...options,
      status: 429,
      details: { ...options.details, retryAfter },
    });
  }
}

export class ServerError extends UnifiedServiceError {
  constructor(
    message: string,
    status: number = 500,
    options: Omit<
      ConstructorParameters<typeof UnifiedServiceError>[2],
      "code"
    > = {},
  ) {
    super(message, ServiceErrorCode.SERVER_ERROR, {
      ...options,
      status,
    });
  }
}

/**
 * Error factory for creating errors from HTTP responses
 */
export class ErrorFactory {
  static fromResponse(response: Response, data?: any): UnifiedServiceError {
    const { status, statusText } = response;
    const message = data?.message || statusText || "Request failed";

    switch (status) {
      case 400:
        return new ValidationError(message, data?.errors || [], {
          status,
          details: data,
        });
      case 401:
        return new AuthenticationError(message, { status, details: data });
      case 403:
        return new AuthorizationError(message, { status, details: data });
      case 404:
        return new NotFoundError(data?.resource || "Resource", data?.id, {
          status,
          details: data,
        });
      case 429:
        return new RateLimitError(data?.retryAfter, { status, details: data });
      case 408:
        return new TimeoutError(message, data?.timeout || 0, {
          status,
          details: data,
        });
      default:
        if (status >= 500) {
          return new ServerError(message, status, { details: data });
        }
        return new UnifiedServiceError(
          message,
          ServiceErrorCode.UNKNOWN_ERROR,
          {
            status,
            details: data,
          },
        );
    }
  }

  static fromFetchError(error: Error, context?: string): UnifiedServiceError {
    if (error.name === "AbortError") {
      return new TimeoutError("Request was aborted", 0, {
        cause: error,
        context,
      });
    }

    if (error.name === "TypeError" && error.message.includes("fetch")) {
      return new NetworkError("Network request failed", {
        cause: error,
        context,
      });
    }

    return new UnifiedServiceError(
      error.message,
      ServiceErrorCode.UNKNOWN_ERROR,
      {
        cause: error,
        context,
      },
    );
  }

  static fromValidation(
    field: string,
    message: string,
    value?: any,
  ): ValidationError {
    return new ValidationError(
      `Validation failed for field '${field}': ${message}`,
      [{ field, message, value }],
    );
  }
}

/**
 * Error handler utility for consistent error processing
 */
export class ErrorHandler {
  /**
   * Process and normalize any error into a UnifiedServiceError
   */
  static normalize(error: unknown, context?: string): UnifiedServiceError {
    if (error instanceof UnifiedServiceError) {
      return error;
    }

    if (error instanceof Error) {
      return new UnifiedServiceError(
        error.message,
        ServiceErrorCode.UNKNOWN_ERROR,
        {
          cause: error,
          context,
        },
      );
    }

    if (typeof error === "string") {
      return new UnifiedServiceError(error, ServiceErrorCode.UNKNOWN_ERROR, {
        context,
      });
    }

    return new UnifiedServiceError(
      "Unknown error occurred",
      ServiceErrorCode.UNKNOWN_ERROR,
      {
        details: error,
        context,
      },
    );
  }

  /**
   * Log error with appropriate level based on severity
   */
  static log(error: UnifiedServiceError, logger: any): void {
    const logData = {
      service: error.service,
      code: error.code,
      status: error.status,
      context: error.context,
      requestId: error.requestId,
      timestamp: error.timestamp,
      details: error.details,
    };

    switch (error.code) {
      case ServiceErrorCode.VALIDATION_ERROR:
      case ServiceErrorCode.NOT_FOUND_ERROR:
        logger.warn(error.message, logData);
        break;
      case ServiceErrorCode.AUTHENTICATION_ERROR:
      case ServiceErrorCode.AUTHORIZATION_ERROR:
        logger.info(error.message, logData);
        break;
      case ServiceErrorCode.RATE_LIMIT_ERROR:
        logger.warn(error.message, logData);
        break;
      case ServiceErrorCode.SERVER_ERROR:
      case ServiceErrorCode.NETWORK_ERROR:
      case ServiceErrorCode.TIMEOUT_ERROR:
        logger.error(error.message, logData);
        break;
      default:
        logger.error(error.message, logData);
    }
  }

  /**
   * Determine if error should be retried
   */
  static shouldRetry(
    error: UnifiedServiceError,
    attempt: number,
    maxAttempts: number,
  ): boolean {
    if (attempt >= maxAttempts) return false;
    if (!error.retry) return false;

    // Additional retry logic based on specific conditions
    switch (error.code) {
      case ServiceErrorCode.RATE_LIMIT_ERROR:
        // Only retry rate limits with specific retry-after
        return !!error.details?.retryAfter;
      case ServiceErrorCode.SERVER_ERROR:
        // Don't retry 501, 502, 503 immediately
        return ![501, 502, 503].includes(error.status || 0);
      default:
        return error.retry;
    }
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  static getRetryDelay(
    attempt: number,
    baseDelay: number = 1000,
    maxDelay: number = 30000,
  ): number {
    const exponentialDelay = Math.min(
      baseDelay * Math.pow(2, attempt - 1),
      maxDelay,
    );
    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 0.1 * exponentialDelay;
    return Math.floor(exponentialDelay + jitter);
  }
}

// Export commonly used error types for convenience
export { ServiceErrorCode, type ServiceError };
